"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Container = void 0;
const inversify_1 = require("inversify");
const symbols_1 = require("./symbols");
const metadata_1 = require("./metadata");
const containersStore = new WeakMap();
const makeFactory = (target) => (context) => {
    const dependenciesValues = (0, metadata_1.getDependencies)(target).map((identifier) => {
        const { container } = context;
        if ((0, metadata_1.isNamed)(identifier)) {
            if (!container.isBoundNamed(identifier.identifier, identifier.name)) {
                throw new Error(`Unbound named dependency ${String(identifier.identifier)}("${identifier.name}") in module ${target.name.replace(/bound\s/g, '')}`);
            }
            return container.getNamed(identifier.identifier, identifier.name);
        }
        if ((0, metadata_1.isMulti)(identifier)) {
            return container.isBound(identifier.identifier) ? container.getAll(identifier.identifier) : [];
        }
        if ((0, metadata_1.isOptional)(identifier)) {
            return container.isBound(identifier.identifier) ? container.get(identifier.identifier) : undefined;
        }
        if (!container.isBound(identifier)) {
            throw new Error(`Unbound dependency ${String(identifier)} in module ${target.name.replace(/bound\s/g, '')}`);
        }
        return container.get(identifier);
    });
    return target(...dependenciesValues);
};
const makeProvider = (container, target) => (context) => target(containersStore.get(context.container));
class Container {
    constructor(container = new inversify_1.Container()) {
        this.container = container;
        this.container.options.defaultScope = 'Singleton';
        containersStore.set(this.container, this);
    }
    bindAll(...identifiers) {
        if (identifiers.length <= 1) {
            return this.createBindSyntax()(...identifiers);
        }
        return {
            toConstantValue: () => {
                throw new Error('toConstantValue() is not supported with multiple identifiers');
            },
            toProvider: () => {
                throw new Error('toProvider() is not supported with multiple identifiers');
            },
            to: (factory) => {
                const bind = this.createBindSyntax();
                let instance;
                function singletonFactory(...args) {
                    if (!instance) {
                        instance = factory(...args);
                    }
                    return instance;
                }
                Object.defineProperty(singletonFactory, 'name', {
                    value: factory.name,
                    configurable: true,
                });
                const withDependenciesFactory = (0, metadata_1.withDependencies)(factory[symbols_1.ModuleMetadataSymbol].dependencies, singletonFactory);
                identifiers.forEach((symbol) => bind(symbol).to(withDependenciesFactory));
                return {
                    whenTargetNamed: () => {
                        throw new Error('whenTargetNamed() is not supported with multiple identifiers');
                    },
                };
            },
        };
    }
    createBindSyntax() {
        return (identifier) => {
            const fullBindToSyntax = this.container.bind(identifier);
            return this.createBindToSyntax(fullBindToSyntax);
        };
    }
    createRebindSyntax() {
        return (identifier) => {
            const fullBindToSyntax = this.container.rebind(identifier);
            return this.createBindToSyntax(fullBindToSyntax);
        };
    }
    createBindToSyntax(fullBindToSyntax) {
        return {
            to: (target) => {
                const fullBindingInWhenOnSyntax = fullBindToSyntax.toDynamicValue(makeFactory(target));
                return this.createBindWhenSyntax(fullBindingInWhenOnSyntax);
            },
            toConstantValue: (target) => {
                const fullBindingInWhenOnSyntax = fullBindToSyntax.toConstantValue(target);
                return this.createBindWhenSyntax(fullBindingInWhenOnSyntax);
            },
            toProvider: (target) => {
                const fullBindingInWhenOnSyntax = fullBindToSyntax.toProvider(makeProvider(this, target));
                return this.createBindWhenSyntax(fullBindingInWhenOnSyntax);
            },
        };
    }
    createBindWhenSyntax(fullBindingInWhenOnSyntax) {
        return {
            whenTargetNamed: (name) => {
                fullBindingInWhenOnSyntax.whenTargetNamed(name);
            },
        };
    }
    get(identifier) {
        return this.container.get(identifier);
    }
    getAsync(identifier) {
        return Promise.resolve(this.container.get(identifier));
    }
    getNamedAsync(identifier, name) {
        return Promise.resolve(this.container.getNamed(identifier, name));
    }
    getAll(identifier) {
        return this.container.isBound(identifier) ? this.container.getAll(identifier) : [];
    }
    getAllAsync(identifier) {
        return Promise.resolve(this.container.isBound(identifier) ? this.container.getAll(identifier) : []);
    }
    getNamed(identifier, name) {
        return this.container.getNamed(identifier, name);
    }
    load(...moduleLoaders) {
        moduleLoaders.forEach((loader) => {
            loader(this.bindAll.bind(this));
        });
    }
    bind(identifier) {
        return this.createBindSyntax()(identifier);
    }
    rebind(identifier) {
        return this.createRebindSyntax()(identifier);
    }
    createChild() {
        return new Container(this.container.createChild());
    }
}
exports.Container = Container;
//# sourceMappingURL=inversify.js.map